/**
 * AnalyticsTab.tsx — the "Company analytics" sub-view of the merged Insights
 * section (#22/#24, 2026-07-21; foldered under Insights in UX Wave 3c).
 *
 * The company-wide view the audit-driven busiest-times report links to: a
 * call-volume / busiest-times heatmap (clinic call attempts + order decisions,
 * summed by Europe/London weekday × hour) plus a per-clinician activity
 * breakdown (callbacks handled / converted / handovers). All COUNTS — this panel
 * carries no money (the money view is the sibling Earnings sub-view). The
 * `days` window comes from the Insights section's SHARED selector (Wave 3c §4)
 * — the same value drives the Earnings sub-view, so the two always compare
 * the same period. Clinician display names resolve through the batched staff
 * lookup, exactly as the Audit tracer does.
 */
import { useCallback, useEffect, useState } from 'react';
import { getCallbackActivity, getClinicianMetrics } from '../api/clinic';
import { lookupStaffEmailMap } from '../api/identity';
import { getOrderActivity } from '../api/orders';
import type { ActivityBucket, ActivityHeatmap, ClinicianMetrics } from '../api/types';
import { mergeBuckets } from '../analytics/buckets';
import { Heatmap } from '../analytics/Heatmap';
import { SectionTitle } from '../staff/ui';
import { DataTable, Td, Th } from '../ui/Table';
import { Card, ErrorState, Help } from '../ui/kit';
import { TableSkeleton } from '../ui/skeletons';
import { DropOffReasonsPanel } from './DropOffReasonsPanel';

export function AnalyticsTab({ days }: { days: number }) {
  const [heat, setHeat] = useState<ActivityBucket[] | null>(null);
  const [metrics, setMetrics] = useState<ClinicianMetrics | null>(null);
  const [names, setNames] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');

  const load = useCallback(() => {
    setStatus('loading');
    // All three reads carry the SAME shared window (`?days=`) so the heatmap
    // and the per-clinician table describe one period.
    Promise.all([getCallbackActivity(days), getOrderActivity(days), getClinicianMetrics(days)])
      .then(([callbackActivity, orderActivity, clinicianMetrics]: [ActivityHeatmap, ActivityHeatmap, ClinicianMetrics]) => {
        setHeat(mergeBuckets(callbackActivity.buckets, orderActivity.buckets));
        setMetrics(clinicianMetrics);
        setStatus('ready');
        // Resolve clinician ids → emails (best-effort; raw id shows on failure).
        lookupStaffEmailMap(clinicianMetrics.clinicians.map((c) => c.clinicianRef))
          .then((map) => (Object.keys(map).length > 0 ? setNames(map) : undefined))
          .catch(() => undefined);
      })
      .catch(() => setStatus('error'));
  }, [days]);

  useEffect(() => {
    load();
  }, [load]);

  // The drop-off ranking (T-2, 2026-09-11) has its OWN read and its own
  // states, and is rendered in EVERY branch below, so a failure on either side
  // never takes the other down.
  //
  // ⚠️ THE `key` IS LOAD-BEARING. The ranking is the second child while the
  // activity reads load or fail and the third once they are ready, and React
  // matches unkeyed children by POSITION — so without a key, the activity reads
  // settling unmounted the ranking and mounted a fresh one: a second fetch, and
  // a failed ranking read silently replaced by a retry nobody pressed.
  const dropOff = <DropOffReasonsPanel key="drop-off-reasons" days={days} />;

  // Wave 6 §1: row-shaped loading for the Insights sub-view.
  if (status === 'loading') {
    return (
      <div style={{ display: 'grid', gap: 'var(--gps-s-4)' }}>
        <TableSkeleton rows={4} columns={4} label="Loading company analytics…" />
        {dropOff}
      </div>
    );
  }
  if (status === 'error' || !heat || !metrics) {
    return (
      <div style={{ display: 'grid', gap: 'var(--gps-s-4)' }}>
        <ErrorState message="Sorry — we could not load the analytics." onRetry={load} />
        {dropOff}
      </div>
    );
  }

  const rows = [...metrics.clinicians].sort((a, b) => b.callbacksHandled - a.callbacksHandled);

  return (
    <div style={{ display: 'grid', gap: 'var(--gps-s-4)' }}>
      <Card>
        <SectionTitle>Busiest times</SectionTitle>
        {heat.length === 0 ? (
          <Help style={{ marginBottom: 0 }}>No activity recorded yet.</Help>
        ) : (
          <Heatmap buckets={heat} ariaLabel="Company activity by day of week and hour, in UK local time" />
        )}
      </Card>

      <Card>
        <SectionTitle>By clinician (last {metrics.windowDays} days)</SectionTitle>
        {rows.length === 0 ? (
          <Help style={{ marginBottom: 0 }}>No clinician activity in this window.</Help>
        ) : (
          <DataTable
            label="Per-clinician activity"
            head={
              <>
                <Th>Clinician</Th>
                <Th style={{ textAlign: 'right' }}>Callbacks worked</Th>
                <Th style={{ textAlign: 'right' }}>Converted</Th>
                <Th style={{ textAlign: 'right' }}>Handovers</Th>
              </>
            }
          >
            {rows.map((c) => (
              <tr key={c.clinicianRef}>
                <Td>{names[c.clinicianRef] ?? c.clinicianRef}</Td>
                <Td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{c.callbacksHandled}</Td>
                <Td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{c.convertedCallbacks}</Td>
                <Td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{c.handovers}</Td>
              </tr>
            ))}
          </DataTable>
        )}
      </Card>

      {/* THE READY BRANCH'S COPY — it is in all three, which is the point: the
          ranking has its own read and its own states, so a failure on either
          side never takes the other down. This one was lost in a rebase once
          and the panel simply vanished the moment the activity reads settled;
          `dropOffReasonsPanel.spec.tsx` is what said so. */}
      {dropOff}
    </div>
  );
}
